<script setup lang="ts">
import { computed } from 'vue'
import { NTooltip } from 'naive-ui'
import { useFormattedDate } from '../composables/useFormattedDate'
import { useNow } from '../composables/useNow'
import type { AgentRepoStatus } from '../stores/agent_repos'

// The git state of a repo's checkout under /workspaces (#1248) — a section of
// the status grid in AdminAgentRepos.vue. Renders as `dt`/`dd` pairs (a Vue 3
// multi-root component) so it slots into the parent's `<dl>` without a wrapper,
// same as AgentRepoDevcontainerStatus.
//
// Why this is on the page at all: sessions leave whatever branch they last
// worked on checked out, and `devcontainer up`/`rebuild` build from that
// working tree. autobattler spent 10 days on a stale dependabot branch while
// merged devcontainer fixes stayed invisible to every rebuild, and nothing here
// hinted at it.
const props = defineProps<{ repo: AgentRepoStatus }>()

const { formatDate, formatAbsoluteDate } = useFormattedDate()
const now = useNow()

const branchLabel = computed(() => props.repo.git_branch ?? '—')

// Amber for a checkout parked off its default branch — the drift that matters.
// Neutral when either side is unknown: "we can't tell" must not read as "on
// the wrong branch".
const onDefaultBranch = computed(() => {
  const { git_branch: branch, git_default_branch: base } = props.repo
  if (!branch || !base) return null
  return branch === base
})
const branchClass = computed(() => {
  if (onDefaultBranch.value === null) return 'text-ink-faint'
  return onDefaultBranch.value ? 'text-ink-muted' : 'text-factory'
})
// Only worth naming the default when the checkout isn't on it.
const branchHint = computed(() => (onDefaultBranch.value === false ? `default: ${props.repo.git_default_branch}` : ''))

const dirtyLabel = computed(() => {
  if (props.repo.git_dirty === null) return 'Unknown'
  return props.repo.git_dirty ? 'Uncommitted changes' : 'Clean'
})
const dirtyClass = computed(() => {
  if (props.repo.git_dirty === null) return 'text-ink-faint'
  return props.repo.git_dirty ? 'text-factory' : 'text-brand-primary'
})

// Counted against a remote-tracking ref that only moves when something
// fetches, and nothing on the box fetches on a schedule — so "0 behind" means
// nothing without the fetch age beside it. The two always render together.
const divergenceLabel = computed(() => {
  const { git_ahead: ahead, git_behind: behind } = props.repo
  if (ahead === null || behind === null) return '—'
  if (ahead === 0 && behind === 0) return 'Up to date'
  return [ahead ? `${ahead} ahead` : '', behind ? `${behind} behind` : ''].filter(Boolean).join(' · ')
})
const divergenceClass = computed(() => (props.repo.git_behind ? 'text-factory' : 'text-ink-muted'))

// A checkout that hasn't fetched in a long time is the actual root cause of a
// misleading "up to date" — call it out rather than showing a bare date.
const STALE_FETCH_AFTER_MS = 24 * 60 * 60 * 1000
const fetchedClass = computed(() => {
  if (!props.repo.git_fetched_at) return 'text-ink-faint'
  const age = Date.now() - new Date(props.repo.git_fetched_at).getTime()
  return age > STALE_FETCH_AFTER_MS ? 'text-factory' : 'text-ink-muted'
})
</script>

<template>
  <dt class="text-ink-faint">Branch</dt>
  <dd class="m-0" :class="branchClass" :data-testid="`agent-repo-branch-${repo.slug}`">
    {{ branchLabel }}
    <span v-if="branchHint" class="text-ink-faint">({{ branchHint }})</span>
  </dd>
  <dt class="text-ink-faint">Working tree</dt>
  <dd class="m-0" :class="dirtyClass" :data-testid="`agent-repo-dirty-${repo.slug}`">{{ dirtyLabel }}</dd>
  <dt class="text-ink-faint">Vs. remote</dt>
  <dd class="m-0 tabular-nums" :class="divergenceClass" :data-testid="`agent-repo-divergence-${repo.slug}`">
    {{ divergenceLabel }}
  </dd>
  <dt class="text-ink-faint">Last fetch</dt>
  <!-- NTooltip rather than a native title: reachable by keyboard focus and
       consistent with the rest of the page's hover help. -->
  <NTooltip v-if="repo.git_fetched_at" placement="top">
    <template #trigger>
      <dd
        class="m-0 tabular-nums cursor-help"
        :class="fetchedClass"
        tabindex="0"
        :data-testid="`agent-repo-fetched-${repo.slug}`"
      >
        {{ formatDate(repo.git_fetched_at, now) }}
      </dd>
    </template>
    {{ formatAbsoluteDate(repo.git_fetched_at) }}
  </NTooltip>
  <dd v-else class="m-0 text-ink-faint" :data-testid="`agent-repo-fetched-${repo.slug}`">Never</dd>
</template>
